/**
 * m3 echo Frontend - Passwort-Zurücksetzungs-Dialog
 *
 * Dialog zum Zurücksetzen eines Benutzerpassworts (nur Admin).
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import { User } from '../../services/admin';

interface PasswordResetDialogProps {
  open: boolean;
  onClose: () => void;
  onReset: () => Promise<{ temporary_password: string }>;
  user: User | null;
}

export const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({
  open,
  onClose,
  onReset,
  user,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const handleReset = async () => {
    setLoading(true);
    setError(null);
    setTempPassword(null);

    try {
      const result = await onReset();
      setTempPassword(result.temporary_password);
    } catch (err: any) {
      console.error('Fehler beim Zurücksetzen des Passworts:', err);
      setError(err.message || 'Fehler beim Zurücksetzen des Passworts');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTempPassword(null);
    setError(null);
    onClose();
  };

  // Temporäres Passwort anzeigen, wenn Zurücksetzen erfolgreich war
  if (tempPassword) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Passwort Erfolgreich Zurückgesetzt</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Passwort wurde erfolgreich zurückgesetzt.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Bitte geben Sie dieses temporäre Passwort an den Benutzer weiter:
          </Typography>
          <Paper
            sx={{
              p: 2,
              mt: 2,
              bgcolor: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              textAlign: 'center',
            }}
          >
            {tempPassword}
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Der Benutzer muss dieses Passwort bei der nächsten Anmeldung ändern.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Bestätigungs-Dialog anzeigen
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Passwort Zurücksetzen</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body1" gutterBottom>
          Sind Sie sicher, dass Sie das Passwort zurücksetzen möchten für:
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
          {user?.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Ein neues temporäres Passwort wird generiert. Der Benutzer muss es bei der nächsten Anmeldung ändern.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleReset}
          variant="contained"
          color="warning"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Passwort Zurücksetzen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetDialog;
